import { GOOGLE_MAP_URL } from "../constants/entities";

const SCRIPT_ID = "googlePlacesScript";

let loadPromise: Promise<void> | null = null;

type MapsWindow = {
  initMap?: () => void;
  google?: { maps?: { places?: unknown } };
};

const mapsWindow = () => window as unknown as MapsWindow;

/**
 * Loads the Google Maps JS API (Places library) exactly once for the app.
 *
 * Idempotent: concurrent and subsequent callers share a single injected
 * script and the same promise. Defines the `initMap` global the API URL's
 * `callback` param points at, resolving when the API signals readiness
 * (or immediately if it is already loaded). The script is never removed —
 * `window.google` persists for the app's lifetime, so tearing the tag down
 * only forced needless refetches.
 */
export function loadGoogleMapsPlaces(): Promise<void> {
  const w = mapsWindow();
  if (w.google?.maps?.places) return Promise.resolve();
  if (loadPromise) return loadPromise;

  loadPromise = new Promise<void>((resolve, reject) => {
    w.initMap = () => resolve();
    const script = document.createElement("script");
    script.id = SCRIPT_ID;
    script.src = GOOGLE_MAP_URL;
    script.async = true;
    script.onerror = () => {
      loadPromise = null;
      reject(new Error("Failed to load the Google Maps JS API"));
    };
    document.body.appendChild(script);
  });

  return loadPromise;
}
